import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TOption } from '@treezor/design-system';

import {
  dashboardUserStatus,
  IDashboardUsersFiltersForm,
  T2FaStatus,
  twoFaStatus,
} from '@/features/dashboard-users/store/support-user.model';
import { IInputField, ISelectField } from '@/ui-kit/table-filters/filters.type';
import { useAccesControl } from '@/hooks/useAccessControl';
import { TAdvancedRoleSlug } from '@/features/roles/store/models/advancedRole.model';
import { useHasFilter } from '@/hooks/useHasFilter';

export interface IUseUsersFiltersProps {
  onChange: (queryParameters: IDashboardUsersFiltersForm) => void;
  initialValues: IDashboardUsersFiltersForm;
}

export default function useDashboradUsersFilters({ onChange, initialValues }: IUseUsersFiltersProps) {
  const { t } = useTranslation();
  const { config: advancedRoles } = useAccesControl();

  const { getValues, reset, setValue, watch } = useForm<IDashboardUsersFiltersForm>({
    defaultValues: initialValues,
  });

  const usernameValue = watch('username');
  const roleValue = watch('role');
  const statusvalue = watch('status');
  const twoFaStatusValue = watch('2faStatus');

  const filtersDeps = JSON.stringify(initialValues);

  useEffect(() => {
    reset(JSON.parse(filtersDeps));
  }, [filtersDeps, reset]);

  const dashboardUserRoleOptions: TOption[] = useMemo(
    () => advancedRoles.map((advancedRole) => ({ value: advancedRole.slug, label: advancedRole.name })),
    [advancedRoles],
  );

  const dashboardUserStatusOptions: TOption[] = useMemo(
    () =>
      Object.keys(dashboardUserStatus).map((status: string) => ({
        label: t(`pages.administration.supportsUsers.table.status.value.${dashboardUserStatus[status]}`),
        value: dashboardUserStatus[status],
      })),
    [t],
  );

  const twoFaStatusOptions: TOption[] = useMemo(
    () =>
      twoFaStatus.map((status) => ({
        value: status,
        label: t(`pages.administration.supportsUsers.table.status.2FA.value.${status}`),
      })),
    [t],
  );

  const handleChange = useCallback(() => {
    onChange(getValues());
  }, [getValues, onChange]);

  const handleValueChange = useCallback(
    (key: keyof IDashboardUsersFiltersForm, value: string | null) => {
      setValue(key, value);
      handleChange();
    },
    [setValue, handleChange],
  );

  const userStatusField: ISelectField = useMemo(
    () => ({
      value: dashboardUserStatusOptions.find((option) => option.value === statusvalue),
      handleChange: (value: string) => handleValueChange('status', value),
      options: dashboardUserStatusOptions,
    }),
    [dashboardUserStatusOptions, handleValueChange, statusvalue],
  );

  const userRoleField: ISelectField = useMemo(
    () => ({
      value: dashboardUserRoleOptions.find((option) => option.value === roleValue),
      handleChange: (roleSlug: TAdvancedRoleSlug) => {
        const advancedRole = advancedRoles.find((role) => role.slug === roleSlug);
        handleValueChange('role', advancedRole?.slug ?? null);
      },
      options: dashboardUserRoleOptions,
    }),
    [advancedRoles, dashboardUserRoleOptions, handleValueChange, roleValue],
  );

  const usernameField: IInputField = useMemo(
    () => ({
      value: usernameValue,
      handleChange: (username: string) => {
        handleValueChange('username', username);
      },
    }),
    [handleValueChange, usernameValue],
  );

  const twoFaField: ISelectField = useMemo(
    () => ({
      value: twoFaStatusOptions.find((option) => twoFaStatusValue === option.value),
      handleChange: (twoFaStatus: T2FaStatus) => {
        handleValueChange('2faStatus', twoFaStatus);
      },
      options: twoFaStatusOptions,
    }),
    [handleValueChange, twoFaStatusOptions, twoFaStatusValue],
  );

  const hasFilters = useHasFilter({
    usernameField,
    userStatusField,
    userRoleField,
    twoFaField,
  });

  const clearFilters = useCallback(() => {
    reset();
    onChange(initialValues);
  }, [reset, initialValues, onChange]);

  return {
    usernameField,
    userStatusField,
    userRoleField,
    twoFaField,
    dashboardUserRoleOptions,
    dashboardUserStatusOptions,
    twoFaStatusOptions,
    clearFilters,
    hasFilters,
  };
}
